// src/components/facility/DeskSurface.js
import React, { useState } from 'react';
import { AnimatePresence } from 'framer-motion';
import { ThreeFacilityMap } from './ThreeFacilityMap';
import { DeskObject } from './DeskObject';
import { DeskPanel } from './DeskPanel';
import { WorkforcePanel } from './panels/WorkforcePanel';
import { FinancePanel } from './panels/FinancePanel';
import { EventsPanel } from './panels/EventsPanel';
import { ResearchPanel } from './panels/ResearchPanel';

function getDeskObjects(state) {
  const unresolvedEvents = (state.activeEvents || []).filter(e => !e.resolved);
  const idleWorkers = state.workers.filter(w => !w.assignedRoomId);

  return [
    {
      key: 'workforce',
      type: 'phone',
      label: 'Workforce',
      badge: `${state.workers.length} staff`,
      hasAlert: idleWorkers.length > 0,
    },
    {
      key: 'finance',
      type: 'ledger',
      label: 'Finances',
      badge: `$${Math.round(state.cash).toLocaleString()}`,
      hasAlert: state.cash < 5000 || state.cash < 0,
    },
    {
      key: 'events',
      type: 'clipboard',
      label: 'Events',
      badge: unresolvedEvents.length > 0 ? `${unresolvedEvents.length} active` : 'Clear',
      hasAlert: unresolvedEvents.length > 0,
    },
    {
      key: 'research',
      type: 'crt',
      label: 'Research',
      badge: state.activeResearch ? state.activeResearch.nodeId : 'Idle',
      hasAlert: !state.activeResearch,
    },
  ];
}

const PANEL_TITLES = {
  workforce: '📞 Workforce',
  finance:   '📒 Finances',
  events:    '📋 Active Events',
  research:  '🖥 Research',
};

export function DeskSurface({ state, dispatch }) {
  const [activePanel, setActivePanel] = useState(null);
  const deskObjects = getDeskObjects(state);

  function closePanel() { setActivePanel(null); }
  function togglePanel(key) { setActivePanel(prev => (prev === key ? null : key)); }

  return (
    <div
      style={{
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        overflow: 'hidden',
        background: '#1a1612',
        backgroundImage: [
          'repeating-linear-gradient(0deg, transparent, transparent 3px, rgba(0,0,0,0.06) 3px, rgba(0,0,0,0.06) 4px)',
          'repeating-linear-gradient(90deg, transparent, transparent 5px, rgba(255,255,255,0.01) 5px, rgba(255,255,255,0.01) 6px)',
        ].join(', '),
        borderRadius: 8,
        border: '1px solid #2a2218',
      }}
    >
      {/* Worn edge vignette */}
      <div style={{
        position: 'absolute', inset: 0, pointerEvents: 'none', borderRadius: 8,
        boxShadow: 'inset 0 0 30px rgba(0,0,0,0.4), inset 0 2px 8px rgba(0,0,0,0.3)',
        zIndex: 1,
      }} />

      {/* Map — fills space above desk strip */}
      <div style={{ flex: 1, minHeight: 0, padding: '10px 10px 6px', position: 'relative', zIndex: 2 }}>
        <div style={{
          height: '100%',
          border: '2px solid #3a3028',
          borderRadius: 6,
          boxShadow: '0 0 0 1px #1a1410, 0 0 12px rgba(0,0,0,0.5), inset 0 0 8px rgba(0,0,0,0.3)',
          overflow: 'hidden',
          background: '#0e1117',
        }}>
          <ThreeFacilityMap state={state} dispatch={dispatch} />
        </div>
      </div>

      {/* Desk object strip */}
      <div style={{
        flexShrink: 0,
        height: 120,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-around',
        padding: '0 12px',
        borderTop: '1px solid #2a2018',
        background: 'rgba(0,0,0,0.2)',
        position: 'relative',
        zIndex: 2,
      }}>
        {deskObjects.map(obj => (
          <DeskObject
            key={obj.key}
            type={obj.type}
            label={obj.label}
            badge={obj.badge}
            hasAlert={obj.hasAlert}
            onClick={() => togglePanel(obj.key)}
          />
        ))}
      </div>

      {/* Panel overlay */}
      <AnimatePresence>
        {activePanel && (
          <DeskPanel
            key={activePanel}
            title={PANEL_TITLES[activePanel]}
            onClose={closePanel}
            state={state}
          >
            {activePanel === 'workforce' && <WorkforcePanel state={state} dispatch={dispatch} />}
            {activePanel === 'finance'   && <FinancePanel   state={state} />}
            {activePanel === 'events'    && <EventsPanel    state={state} dispatch={dispatch} />}
            {activePanel === 'research'  && <ResearchPanel  state={state} dispatch={dispatch} />}
          </DeskPanel>
        )}
      </AnimatePresence>
    </div>
  );
}
